import React, { createContext, useContext, useState, useCallback } from 'react';

// Supported languages with their details (kept for future use)
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', native: 'English', flag: '🇺🇸' },
  es: { name: 'Spanish', native: 'Español', flag: '🇪🇸' },
  pt: { name: 'Portuguese', native: 'Português', flag: '🇧🇷' },
  de: { name: 'German', native: 'Deutsch', flag: '🇩🇪' },
  fr: { name: 'French', native: 'Français', flag: '🇫🇷' },
  tr: { name: 'Turkish', native: 'Türkçe', flag: '🇹🇷' },
  ru: { name: 'Russian', native: 'Русский', flag: '🇷🇺' },
  it: { name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
  nl: { name: 'Dutch', native: 'Nederlands', flag: '🇳🇱' },
  pl: { name: 'Polish', native: 'Polski', flag: '🇵🇱' },
  zh: { name: 'Chinese', native: '中文', flag: '🇨🇳' },
};

const TranslationContext = createContext(null);

/**
 * TRANSLATION SYSTEM DISABLED
 * 
 * The AI translation system has been disabled to improve site performance.
 * The DOM-based translation was causing slowdowns after several page visits.
 * 
 * This provider now only maintains the language selector UI state.
 * All translation logic (DOM traversal, API calls, batching) has been removed.
 * 
 * To re-enable in the future:
 * 1. Restore the translation logic from git history
 * 2. Consider server-side translation instead of client-side DOM manipulation
 */

export function TranslationProvider({ children }) {
  // Always English - translation disabled
  const [language] = useState('en');
  
  // No translation state needed
  const isTranslating = false;
  const translationStatus = '';

  // Change language - shows message instead of translating
  const changeLanguage = useCallback((newLang) => {
    if (newLang === 'en') {
      // Already English, do nothing
      return { success: true };
    }
    
    // Return info that translation is not available
    return { 
      success: false, 
      message: 'This language will be available soon.' 
    };
  }, []);

  // No-op function for compatibility
  const runTranslation = useCallback(() => {
    // Translation disabled - no-op
  }, []);

  const value = {
    currentLanguage: language,
    changeLanguage,
    isTranslating,
    translationStatus,
    runTranslation,
    languages: SUPPORTED_LANGUAGES,
    isDisabled: true, // Flag to indicate translation is disabled
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    return {
      currentLanguage: 'en',
      changeLanguage: () => ({ success: false, message: 'This language will be available soon.' }),
      isTranslating: false,
      translationStatus: '',
      runTranslation: () => {},
      languages: SUPPORTED_LANGUAGES,
      isDisabled: true,
    };
  }
  return context;
}

export default TranslationContext;
